import { useQuery } from '@tanstack/react-query';
import { apiClient, APIResponse } from '../client';

export interface InventoryItem {
  _id: string;
  ingredientId: string;
  ingredientName: string;
  branchId: string;
  currentStock: number;
  unit: string;
  reorderLevel: number;
  maxLevel: number;
  lastUpdated: string;
}

export interface InventoryBatch {
  _id: string;
  ingredientId: string;
  ingredientName: string;
  batchNumber: string;
  quantity: number;
  unit: string;
  expiryDate: string;
  receivedDate: string;
  supplierBatchNumber?: string;
}

export interface InventoryEvent {
  _id: string;
  branchId: string;
  ingredientId: string;
  ingredientName: string;
  eventType: 'sale' | 'adjustment' | 'transfer_in' | 'transfer_out' | 'waste' | 'purchase';
  quantity: number;
  unit: string;
  reason?: string;
  referenceId?: string;
  createdBy: string;
  createdAt: string;
}

export interface LowStockAlert {
  ingredientId: string;
  ingredientName: string;
  currentStock: number;
  reorderLevel: number;
  unit: string;
}

export interface ExpiryAlert {
  batchId: string;
  ingredientName: string;
  batchNumber: string;
  expiryDate: string;
  daysUntilExpiry: number;
  quantity: number;
  unit: string;
}

// Fetch inventory items for a branch
export function useInventoryItems(branchId?: string) {
  return useQuery({
    queryKey: ['inventory', 'items', branchId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId);

      const response = await apiClient.get<APIResponse<{ items: InventoryItem[] }>>(
        `/inventory/items?${params.toString()}`
      );
      return response.data.data.items;
    },
    enabled: !!branchId,
  });
}

// Fetch inventory batches with optional expiry filter
export function useInventoryBatches(branchId?: string, expiryDays?: number) {
  return useQuery({
    queryKey: ['inventory', 'batches', branchId, expiryDays],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId);
      if (expiryDays) params.append('expiryDays', expiryDays.toString());

      const response = await apiClient.get<APIResponse<{ batches: InventoryBatch[] }>>(
        `/inventory/batches?${params.toString()}`
      );
      return response.data.data.batches;
    },
    enabled: !!branchId,
  });
}

// Fetch inventory events (audit log)
export function useInventoryEvents(branchId?: string, ingredientId?: string) {
  return useQuery({
    queryKey: ['inventory', 'events', branchId, ingredientId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId);
      if (ingredientId) params.append('ingredientId', ingredientId);

      const response = await apiClient.get<APIResponse<{ events: InventoryEvent[] }>>(
        `/inventory/events?${params.toString()}`
      );
      return response.data.data.events;
    },
    enabled: !!branchId,
  });
}

// Fetch low stock alerts
export function useLowStockAlerts(branchId?: string) {
  return useQuery({
    queryKey: ['inventory', 'alerts', 'low-stock', branchId],
    queryFn: async () => {
      const response = await apiClient.get<APIResponse<{ lowStockAlerts: LowStockAlert[]; expiryAlerts: ExpiryAlert[] }>>(
        `/inventory/alerts?branchId=${branchId}`
      );
      return response.data.data.lowStockAlerts || [];
    },
    enabled: !!branchId,
  });
}

// Fetch expiry alerts
export function useExpiryAlerts(branchId?: string, days: number = 7) {
  return useQuery({
    queryKey: ['inventory', 'alerts', 'expiry', branchId, days],
    queryFn: async () => {
      const response = await apiClient.get<APIResponse<{ lowStockAlerts: LowStockAlert[]; expiryAlerts: ExpiryAlert[] }>>(
        `/inventory/alerts?branchId=${branchId}`
      );
      return response.data.data.expiryAlerts || [];
    },
    enabled: !!branchId,
  });
}

// Ingredient types
export interface Ingredient {
  _id: string;
  name: string;
  sku: string;
  category: string;
  unit: string;
  minStockLevel?: number;
  maxStockLevel?: number;
  reorderPoint?: number;
  isComposite?: boolean;
  components?: {
    ingredientId: string | { _id: string; name: string; sku: string };
    quantity: number;
    unit: string;
  }[];
  yieldQuantity?: number;
  yieldUnit?: string;
  // Purchasing unit configuration
  purchaseUnit?: string;
  purchaseUnitSize?: number;
  estimatedPricePerPurchaseUnit?: number;
  isActive: boolean;
  createdAt: string;
}

// Fetch all ingredients
export function useIngredients(category?: string) {
  return useQuery({
    queryKey: ['ingredients', category],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (category) params.append('category', category);

      const response = await apiClient.get<APIResponse<{ ingredients: Ingredient[] }>>(
        `/inventory/ingredients?${params.toString()}`
      );
      return response.data.data.ingredients;
    },
  });
}

// Fetch single ingredient
export function useIngredient(id: string) {
  return useQuery({
    queryKey: ['ingredient', id],
    queryFn: async () => {
      const response = await apiClient.get<APIResponse<{ ingredient: Ingredient }>>(
        `/inventory/ingredients/${id}`
      );
      return response.data.data.ingredient;
    },
    enabled: !!id,
  });
}

// Fetch inventory for a specific branch
export function useInventory(branchId?: string) {
  return useQuery({
    queryKey: ['inventory', branchId],
    queryFn: async () => {
      if (!branchId) {
        return [];
      }
      const response = await apiClient.get<APIResponse<{ inventory: any[] }>>(
        `/inventory?branchId=${branchId}`
      );
      return response.data.data.inventory;
    },
    enabled: !!branchId && branchId !== 'undefined',
  });
}
